/* eslint-disable react/jsx-indent */
import {useState, useEffect, type ReactNode} from 'react'
import _ from 'lodash'
import {ListSubheader, MenuItem, TextField} from '@mui/material'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'

export const statesByCountryCodeCache = {}

interface FormikSelectProps {
    id: string
    formik: any
    options: unknown[]
    placeholder?: string
    className?: string
    onChange?(...args: unknown[]): unknown
    onKeyDown?(...args: unknown[]): unknown
    focusHandler?(...args: unknown[]): unknown
    blurHandler?(...args: unknown[]): unknown
    error?: boolean
    autoFocus?: boolean
    MenuClassName?: string
    fullWidth?: boolean
    disabled?: boolean
    popup?: boolean
    renderValue?: (value: unknown) => ReactNode
    isMaxWidthEnabled?: boolean
    position?: any
}

// For Airline Strategic Dashboard - Added renderValue property for showing the selected date range in calendar
const FormikSelect = ({
    id,
    formik,
    options,
    placeholder,
    className,
    onChange,
    MenuClassName,
    onKeyDown,
    error,
    focusHandler,
    blurHandler,
    autoFocus,
    fullWidth,
    disabled,
    popup,
    renderValue,
    isMaxWidthEnabled,
    position
}: FormikSelectProps) => {
    const [isOpen, setIsOpen] = useState(false)

    useEffect(() => {
        if (!popup) {
            window.addEventListener('scroll', () => setIsOpen(false))
        }
        return () => {
            window.removeEventListener('scroll', () => setIsOpen(false))
        }
    }, [popup])

    const changeHandler = (event) => {
        formik.setFieldValue(id, event.target.value)
        if (onChange) {
            onChange(event)
        }
    }
    const menuItemStyle = {
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        '&:hover': {
            color: '#356eab',
            backgroundColor: 'inherit'
        }
    }

    const handleKeyUp = (event) => {
        /**
         * Adding a check if the current id is for the phoneCode as this function is only
         * required for the phone drop down field
         */
        const isFieldPhoneCode = _.includes(_.toLower(id), 'phonecode')
        if (isFieldPhoneCode && event.key !== 'Tab' && event.key !== 'Enter') {
            if (event.keyCode >= 65 && event.keyCode <= 90) {
                const parts = event.target.outerText.split('+')
                if (parts.length === 2) {
                    const country = parts[0].trim()
                    const code = parts[1].trim()
                    if (country && code && !isNaN(Number(code))) {
                        formik.setFieldValue(id, `${code}:${country}`)
                    }
                } else if (parts.length === 1) {
                    formik.setFieldValue(id, parts[0])
                }
            }
            if (onKeyDown) {
                const flag = onKeyDown(event)
                if (typeof flag === 'boolean') {
                    setIsOpen(flag)
                }
            }
        }
    }

    /**
     * COMMENTING THE KEYDOWN EVENT AS IT IS WAS CAUSING ISSUE FOR THE KEYBOARD EVENTS.
     */
    // const keyHandler = (event) => {
    //     if (event.key === 'Enter') {
    //         const changeEvent = {
    //             target: {
    //                 name: id,
    //                 value: _.get(formik.values, id)
    //             }
    //         }
    //         changeHandler(changeEvent)
    //     } else if (
    //         (event.keyCode >= 65 && event.keyCode <= 90) ||
    //         event.key === 'ArrowDown' ||
    //         event.key === 'ArrowUp'
    //     ) {
    //         const value = _.get(formik.values, id)
    //         const index = _.findIndex(options, (obj: any) => obj.value === value)
    //         if (event.key === 'ArrowDown') {
    //             formik.setFieldValue(id, options[(index + 1) % options.length].value)
    //         } else if (event.key === 'ArrowUp') {
    //             if (placeholder?.length > 0) {
    //                 formik.setFieldValue(id, index > 0 ? options[index - 1].value : '-Select-')
    //             } else {
    //                 formik.setFieldValue(id, index > 0 ? options[index - 1].value : options[0].value)
    //             }
    //         }
    //         setIsOpen(true)
    //     }
    // }

    return options?.length ? (
        <TextField
            variant='outlined'
            autoFocus={autoFocus}
            name={id}
            select
            value={_.get(formik.values, id)}
            onChange={changeHandler}
            onKeyUp={handleKeyUp}
            // onKeyDown={keyHandler}
            onFocus={focusHandler}
            onBlur={() => {
                formik.setFieldTouched(id)
                if (blurHandler) blurHandler()
            }}
            error={(_.get(formik.touched, id) && Boolean(_.get(formik.errors, id))) || error}
            helperText={
                _.get(formik.touched, id) &&
                !!_.get(formik.errors, id) && (
                    <>
                        <ErrorOutlineOutlinedIcon className='formik-error__info-icon' />
                        {_.get(formik.errors, id)}
                    </>
                )
            }
            className={`${className} dropdown-field`}
            fullWidth={fullWidth}
            SelectProps={{
                open: isOpen,
                onClose: () => setIsOpen(false),
                onOpen: () => setIsOpen(true),
                renderValue,
                MenuProps: {
                    style: isMaxWidthEnabled ? {maxWidth: 'fit-content'} : {},
                    className: MenuClassName,
                    disableScrollLock: true,
                    PaperProps: {
                        style: {
                            maxHeight: 300
                        }
                    }
                },
                inputProps: {
                    id
                }
            }}
            disabled={disabled}
        >
            <MenuItem key='-Select-' value='-Select-' disabled>
                {placeholder ?? '-Select-'}
            </MenuItem>
            {options?.map((option: any, index) =>
                option.groupHead ? (
                    <ListSubheader className={option.subClassName} key={`${option.value}-${index}`}>
                        {option.label}
                    </ListSubheader>
                ) : (
                    <MenuItem
                        key={`${option.value}-${index}`}
                        value={option.value}
                        disabled={option.disabled}
                        sx={menuItemStyle}
                    >
                        {option.label}
                    </MenuItem>
                )
            )}
        </TextField>
    ) : null
}

FormikSelect.defaultProps = {
    placeholder: '-Select-',
    className: '',
    onChange: undefined,
    focusHandler: undefined,
    onKeyDown: undefined,
    blurHandler: undefined,
    autoFocus: false,
    MenuClassName: '',
    fullWidth: false,
    disabled: false,
    popup: false,
    renderValue: undefined,
    isMaxWidthEnabled: false,
    position: 'right-start'
}

export default FormikSelect
